import { useState, useCallback } from 'react';
import { Lightbulb, Sparkles, Check, X, ChevronDown, ChevronUp, Loader as Loader2, Building2, MapPin, Users as UsersIcon, TrendingUp, Shield, CircleAlert as AlertCircle } from 'lucide-react';
import { canInvokeEdgeFn, releaseEdgeFn } from '../../lib/edgeFnGuard';
import { useApi } from '../../lib/useApi';
import type { WhitespaceSuggestion } from '../../types';

interface TeamMember {
  user_id: string;
  user_name: string;
  user_email: string;
}

interface Props {
  orgId: string;
  userId: string;
  suggestions: WhitespaceSuggestion[];
  teamMembers: TeamMember[];
  onSuggestionsChanged: () => void;
}

function ScoreBadge({ label, score }: { label: string; score: number }) {
  const color =
    score >= 8 ? 'bg-app-positive/10 text-app-positive border-app-positive' :
    score >= 6 ? 'bg-app-warning/10 text-app-warning border-app-warning' :
    'bg-app-canvas text-app-muted border-app-line';
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold ${color}`}>
      <span className="text-[10px] font-medium opacity-70">{label}</span>
      <span>{score.toFixed(0)}/10</span>
    </div>
  );
}

function ConfidencePill({ level }: { level: string }) {
  const styles: Record<string, string> = {
    high: 'bg-app-positive/10 text-app-positive',
    medium: 'bg-app-warning/10 text-app-warning',
    low: 'bg-app-critical/10 text-app-critical',
  };
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${styles[level] || styles.medium}`}>
      {level}
    </span>
  );
}

function SuggestionCard({
  suggestion,
  teamMembers,
  onAccept,
  onReject,
  accepting,
  rejecting,
}: {
  suggestion: WhitespaceSuggestion;
  teamMembers: TeamMember[];
  onAccept: (id: string, assigneeId: string) => void;
  onReject: (id: string) => void;
  accepting: boolean;
  rejecting: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [assigneeId, setAssigneeId] = useState('');
  const busy = accepting || rejecting;
  const empCount = suggestion.firmographic_signals?.employee_count;

  return (
    <div className="bg-app-surface rounded-lg border border-app-line overflow-hidden transition-shadow hover:shadow-none">
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2.5 mb-1">
              <h4 className="text-sm font-semibold text-app-ink truncate">{suggestion.company_name}</h4>
              <ConfidencePill level={suggestion.confidence_level} />
            </div>
            <div className="flex items-center gap-3 text-xs text-app-muted">
              {suggestion.industry && (
                <span className="inline-flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  {suggestion.industry}
                </span>
              )}
              {empCount && empCount > 0 && (
                <span className="inline-flex items-center gap-1">
                  <UsersIcon className="w-3 h-3" />
                  {empCount.toLocaleString()} employees
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <ScoreBadge label="ICP" score={suggestion.icp_match_score} />
            <ScoreBadge label="Propensity" score={suggestion.propensity_score} />
          </div>
        </div>

        <p className="text-xs text-app-muted mt-3 leading-relaxed">{suggestion.reasoning}</p>

        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-forest-600 hover:text-forest-700 transition-colors"
        >
          {expanded ? 'Hide details' : 'View detailed analysis'}
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>

        {expanded && (
          <div className="mt-3 space-y-3 pt-3 border-t border-app-line">
            {suggestion.ai_reasoning_detailed && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-app-muted mb-1">Analysis</p>
                <p className="text-xs text-app-muted leading-relaxed whitespace-pre-line">{suggestion.ai_reasoning_detailed}</p>
              </div>
            )}
            {suggestion.buyer_persona_match && (
              <div className="flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 text-app-muted mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-app-muted mb-0.5">Target Persona</p>
                  <p className="text-xs text-app-muted">{suggestion.buyer_persona_match}</p>
                </div>
              </div>
            )}
            {suggestion.competitive_landscape && (
              <div className="flex items-start gap-2">
                <Shield className="w-3.5 h-3.5 text-app-muted mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-app-muted mb-0.5">Competitive Landscape</p>
                  <p className="text-xs text-app-muted">{suggestion.competitive_landscape}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="px-5 py-3 bg-app-canvas/60 border-t border-app-line flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-app-muted flex-shrink-0">Assign to</label>
          <select
            value={assigneeId}
            onChange={e => setAssigneeId(e.target.value)}
            disabled={busy}
            className="text-xs border border-app-line rounded-lg px-2.5 py-1.5 bg-app-surface focus:outline-none focus:ring-2 focus:ring-forest-500/30 focus:border-forest-500 max-w-[200px] truncate"
          >
            <option value="">Select team member...</option>
            {teamMembers.map(m => (
              <option key={m.user_id} value={m.user_id}>
                {m.user_name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => onReject(suggestion.id)}
            disabled={busy}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-app-muted bg-app-surface border border-app-line rounded-lg hover:bg-app-canvas hover:text-app-critical hover:border-app-critical transition disabled:opacity-50"
          >
            {rejecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
            Reject
          </button>
          <button
            onClick={() => onAccept(suggestion.id, assigneeId)}
            disabled={busy || !assigneeId}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-forest-600 rounded-lg hover:bg-forest-700 transition-colors disabled:opacity-50"
          >
            {accepting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WhitespaceSuggestions({ orgId, suggestions, teamMembers, onSuggestionsChanged }: Props) {
  const { apiPost } = useApi();
  const [generating, setGenerating] = useState(false);
  const [actionIds, setActionIds] = useState<Record<string, 'accepting' | 'rejecting'>>({});
  const [error, setError] = useState('');

  const handleGenerate = useCallback(async () => {
    if (!canInvokeEdgeFn('whitespace-suggest')) return;
    setGenerating(true);
    setError('');
    try {
      await apiPost('/v1/whitespace-suggestions/suggest', { org_id: orgId, count: 5 });
      onSuggestionsChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate suggestions');
    } finally {
      setGenerating(false);
      releaseEdgeFn('whitespace-suggest');
    }
  }, [orgId, onSuggestionsChanged, apiPost]);

  const handleAccept = useCallback(async (suggestionId: string, assigneeId: string) => {
    setActionIds(prev => ({ ...prev, [suggestionId]: 'accepting' }));
    setError('');
    try {
      await apiPost<{ account_id?: string }>('/v1/accept-whitespace-suggestion', {
        action: 'accept',
        suggestion_id: suggestionId,
        assigned_to_user_id: assigneeId,
        org_id: orgId,
      });

      onSuggestionsChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept suggestion');
    } finally {
      setActionIds(prev => {
        const next = { ...prev };
        delete next[suggestionId];
        return next;
      });
    }
  }, [orgId, onSuggestionsChanged, apiPost]);

  const handleReject = useCallback(async (suggestionId: string) => {
    setActionIds(prev => ({ ...prev, [suggestionId]: 'rejecting' }));
    setError('');
    try {
      await apiPost('/v1/accept-whitespace-suggestion', {
        action: 'reject',
        suggestion_id: suggestionId,
        org_id: orgId,
      });

      onSuggestionsChanged();

      const remaining = suggestions.filter(s => s.id !== suggestionId && s.status === 'suggested').length;
      if (remaining < 5) {
        apiPost('/v1/whitespace-suggestions/suggest', { org_id: orgId, count: 5 }).then(() => {
          setTimeout(onSuggestionsChanged, 2000);
        }).catch(() => {});
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject suggestion');
    } finally {
      setActionIds(prev => {
        const next = { ...prev };
        delete next[suggestionId];
        return next;
      });
    }
  }, [orgId, suggestions, onSuggestionsChanged, apiPost]);

  const pendingSuggestions = suggestions.filter(s => s.status === 'suggested');
  const showGenerate = pendingSuggestions.length < 5;

  return (
    <div className="bg-app-surface rounded-lg border border-app-line overflow-hidden">
      <div className="px-6 py-4 border-b border-app-line flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-app-warning/10 rounded-lg flex items-center justify-center">
            <Lightbulb className="w-4 h-4 text-app-warning" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-app-ink">Whitespace Suggestions</h3>
            <p className="text-[11px] text-app-muted">Evidence-led accounts matched to your territory ICP</p>
          </div>
        </div>
        {showGenerate && (
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-forest-600 rounded-lg hover:bg-forest-700 transition-colors disabled:opacity-50"
          >
            {generating ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5" />
                Generate Suggestions
              </>
            )}
          </button>
        )}
      </div>

      {error && (
        <div className="mx-6 mt-4 flex items-center gap-2 p-3 bg-app-critical/10 border border-app-critical rounded-lg">
          <AlertCircle className="w-4 h-4 text-app-critical flex-shrink-0" />
          <p className="text-xs text-app-critical">{error}</p>
        </div>
      )}

      {generating && pendingSuggestions.length === 0 && (
        <div className="p-12 text-center">
          <Loader2 className="w-8 h-8 text-forest-500 animate-spin mx-auto mb-3" />
          <p className="text-sm font-medium text-app-ink">Analysing territory parameters</p>
          <p className="text-xs text-app-muted mt-1">Identifying high-fit prospects with strong propensity to buy...</p>
        </div>
      )}

      {!generating && pendingSuggestions.length === 0 && (
        <div className="p-12 text-center">
          <div className="w-12 h-12 bg-app-canvas rounded-lg flex items-center justify-center mx-auto mb-3">
            <TrendingUp className="w-6 h-6 text-app-line-strong" />
          </div>
          <p className="text-sm font-medium text-app-muted">No suggestions yet</p>
          <p className="text-xs text-app-muted mt-1">
            Generate evidence-led account suggestions from your territory configuration and vendor fit.
          </p>
        </div>
      )}

      {pendingSuggestions.length > 0 && (
        <div className="p-4 space-y-3">
          {pendingSuggestions.map(s => (
            <SuggestionCard
              key={s.id}
              suggestion={s}
              teamMembers={teamMembers}
              onAccept={handleAccept}
              onReject={handleReject}
              accepting={actionIds[s.id] === 'accepting'}
              rejecting={actionIds[s.id] === 'rejecting'}
            />
          ))}
        </div>
      )}
    </div>
  );
}
